import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../../../services/api';
import Chip from '../shared/Chip';
import './OnboardingOverlay.css';

const TOTAL_STEPS = 3;

const STEP_META = [
  {
    title: '1. Add a Dataset',
    hint: 'Click + in Available Datasets, or drop files on the chat.',
  },
  {
    title: '2. Wait for Published',
    hint: 'New uploads process first. You can only ask once the status turns green.',
  },
  {
    title: '3. Select and Ask',
    hint: 'Check a published dataset, then type your question below.',
  },
];

function OnboardingOverlay({ onComplete, onShowNotification }) {
  const [step, setStep] = useState(1);
  const [completing, setCompleting] = useState(false);
  const nextButtonRef = useRef(null);

  // Move focus to the primary advance button on each step so keyboard users
  // can hit Enter to progress without tabbing.
  useEffect(() => {
    if (nextButtonRef.current) nextButtonRef.current.focus();
  }, [step]);

  const handleBack = useCallback(() => {
    setStep((s) => Math.max(1, s - 1));
  }, []);

  const handleNext = useCallback(() => {
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  }, []);

  const handleDone = useCallback(async () => {
    if (completing) return;
    setCompleting(true);
    try {
      await apiService.completeOnboarding();
      onComplete?.();
    } catch (err) {
      setCompleting(false);
      onShowNotification?.(err.message || 'Could not finish onboarding. Please try again.', 'error');
    }
  }, [completing, onComplete, onShowNotification]);

  const meta = STEP_META[step - 1];
  const isLast = step === TOTAL_STEPS;

  return (
    <div
      className="onboarding-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      <div className="onboarding-card">
        <div className="onboarding-header">
          <div>
            <div className="onboarding-eyebrow">QUICK START</div>
            <h2 id="onboarding-title" className="onboarding-title">Get your first answer</h2>
          </div>
          <div className="onboarding-progress" aria-label={`Step ${step} of ${TOTAL_STEPS}`}>
            {[1, 2, 3].map((n) => (
              <span
                key={n}
                className={`onboarding-progress-bar ${n <= step ? 'is-active' : ''}`}
              />
            ))}
          </div>
        </div>

        <div className="onboarding-step-row">
          <span className="onboarding-step-title">{meta.title}</span>
          <span className="onboarding-step-hint">{meta.hint}</span>
        </div>

        <div className="onboarding-body">
          <div className="onboarding-canvas">
            {step === 1 && <Step1Canvas />}
            {step === 2 && <Step2Canvas />}
            {step === 3 && <Step3Canvas />}
          </div>

          <aside className="onboarding-sidebar">
            <div className="onboarding-sidebar-header">
              <span>Available Datasets</span>
              <span className="onboarding-plus" aria-hidden="true">+</span>
            </div>
            <div className="onboarding-sidebar-body">
              {step === 1 && (
                <p className="onboarding-sidebar-empty">
                  No datasets available. Create a dataset to get started.
                </p>
              )}
              {step === 2 && (
                <>
                  <SidebarItem label="New upload" status="Processing" />
                  <SidebarItem label="Health records" status="Published" />
                </>
              )}
              {step === 3 && (
                <>
                  <SidebarItem label="Health records" status="Published" checked />
                  <SidebarItem label="New upload" status="Processing" />
                </>
              )}
            </div>
          </aside>
        </div>

        <div className="onboarding-footer">
          {step > 1 ? (
            <button
              type="button"
              className="onboarding-btn onboarding-btn-secondary"
              onClick={handleBack}
            >
              Back
            </button>
          ) : (
            <span aria-hidden="true" />
          )}
          {isLast ? (
            <button
              ref={nextButtonRef}
              type="button"
              className="onboarding-btn onboarding-btn-primary"
              onClick={handleDone}
              disabled={completing}
            >
              {completing ? 'Finishing…' : 'Done'}
            </button>
          ) : (
            <button
              ref={nextButtonRef}
              type="button"
              className="onboarding-btn onboarding-btn-primary"
              onClick={handleNext}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Step1Canvas() {
  return (
    <>
      <div className="onboarding-note">Attach a dataset before asking.</div>
      <div className="onboarding-dropzone">
        <div className="onboarding-dropzone-title">Drop files here</div>
        <div className="onboarding-dropzone-sub">or use the + button in Available Datasets</div>
      </div>
      <div className="onboarding-spacer" />
      <DemoAskBox placeholder="Ask a question about selected datasets" disabled />
    </>
  );
}

function Step2Canvas() {
  return (
    <>
      <div className="onboarding-file-row">
        <div>
          <div className="onboarding-file-name">New upload</div>
          <div className="onboarding-file-sub">Wait before asking questions.</div>
        </div>
        <Chip variant="processing" className="onboarding-pill">Processing</Chip>
      </div>
      <hr className="onboarding-divider" />
      <div className="onboarding-file-row">
        <div>
          <div className="onboarding-file-name">Health records</div>
          <div className="onboarding-file-sub">Ready to select in Ask ResAI.</div>
        </div>
        <Chip variant="success" className="onboarding-pill">Published</Chip>
      </div>
      <div className="onboarding-spacer" />
      <DemoAskBox placeholder="Ask a question about selected datasets" disabled />
    </>
  );
}

function Step3Canvas() {
  return (
    <>
      <div className="onboarding-answer-placeholder">Your answer will appear here.</div>
      <div className="onboarding-spacer" />
      <div className="onboarding-using">
        <span className="onboarding-using-label">Using:</span>
        <span className="onboarding-using-pill">Health records</span>
      </div>
      <DemoAskBox placeholder="Ask a question about the selected dataset" />
    </>
  );
}

function SidebarItem({ label, status, checked = false }) {
  return (
    <div className="onboarding-sidebar-item">
      <span
        className={`onboarding-checkbox ${checked ? 'is-checked' : ''}`}
        aria-hidden="true"
      />
      <div className="onboarding-sidebar-item-text">
        <div className="onboarding-sidebar-item-name">{label}</div>
        <div className="onboarding-sidebar-item-status">{status}</div>
      </div>
    </div>
  );
}

function DemoAskBox({ placeholder, disabled = false }) {
  return (
    <div className={`onboarding-ask ${disabled ? 'is-disabled' : ''}`}>
      <input
        className="onboarding-ask-input"
        placeholder={placeholder}
        tabIndex={-1}
        readOnly
      />
      <span className="onboarding-ask-send" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 11.5V2.5M7 2.5L3 6.5M7 2.5L11 6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </div>
  );
}

export default OnboardingOverlay;
